import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Upload, 
  Play, 
  TrendingUp, 
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Activity
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { formatDate } from '../utils/formatters';

interface DashboardData {
  documents: {
    total: number;
    golden: number;
    pending: number;
    byStatus: Record<string, number>;
    byType: Record<string, number>;
  };
  tests: {
    total: number;
    recent: Array<{
      id: string;
      status: string;
      totalDocuments: number;
      successfulDocuments: number;
      overallAccuracy: number;
      startedAt: string;
      completedAt: string | null;
    }>;
    latestAccuracy: number;
    lastRunDate: string | null;
  };
  statistics: {
    averageInitialAccuracy: number;
    readyForTesting: boolean;
  };
}

export const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await apiClient.get('/api/dashboard');
      setData(response.data.dashboard);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card h-32"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card h-96"></div>
          <div className="card h-96"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Nepodařilo se načíst data dashboardu</p>
      </div>
    );
  }

  const stats = [
    {
      name: 'Celkem dokumentů',
      value: data.documents.total,
      icon: FileText,
      color: 'bg-blue-500',
      href: '/documents'
    },
    {
      name: 'Golden dokumenty',
      value: data.documents.golden,
      icon: CheckCircle,
      color: 'bg-green-500',
      href: '/documents?status=golden'
    },
    {
      name: 'Čeká na validaci',
      value: data.documents.pending,
      icon: Clock,
      color: 'bg-yellow-500',
      href: '/documents?status=pending'
    },
    {
      name: 'Celkem testů',
      value: data.tests.total,
      icon: Activity,
      color: 'bg-purple-500',
      href: '/history'
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'running':
        return <Clock className="h-5 w-5 text-yellow-500 animate-spin" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="badge-success">Dokončeno</span>;
      case 'running':
        return <span className="badge-warning">Probíhá</span>;
      case 'failed':
        return <span className="badge-error">Selhalo</span>;
      default:
        return <span className="badge-gray">Neznámé</span>;
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Přehled</h1>
        <p className="text-gray-600 mt-2">
          Sledujte pokrok testování dokumentů a metriky přesnosti
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.name}
              to={stat.href}
              className="card hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-center">
                <div className={`p-3 rounded-full ${stat.color}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Link
          to="/upload"
          className="card hover:shadow-md transition-shadow cursor-pointer border-2 border-dashed border-gray-200 hover:border-primary-300"
        >
          <div className="text-center">
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nahrát dokumenty</h3>
            <p className="text-gray-500">Přidejte nové dokumenty pro testování</p>
          </div>
        </Link>

        <Link
          to="/documents?status=pending"
          className="card hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="text-center">
            <CheckCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Validovat dokumenty</h3>
            <p className="text-gray-500">Zkontrolujte a validujte vytěžená data</p>
          </div>
        </Link>

        <Link
          to="/test"
          className={`card hover:shadow-md transition-shadow cursor-pointer ${
            data.statistics.readyForTesting 
              ? 'border-green-200 bg-green-50' 
              : 'opacity-50 cursor-not-allowed'
          }`}
        >
          <div className="text-center">
            <Play className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Spustit test</h3>
            <p className="text-gray-500">
              {data.statistics.readyForTesting 
                ? 'Zahájit testování přesnosti' 
                : 'Nejdříve potřebujete golden dokumenty'}
            </p>
          </div>
        </Link>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tests */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Poslední testy</h3>
            <Link to="/history" className="text-sm text-primary-600 hover:text-primary-500">
              Zobrazit vše
            </Link>
          </div>
          
          {data.tests.recent.length === 0 ? (
            <div className="text-center py-8">
              <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Zatím nebyl spuštěn žádný test</p>
            </div>
          ) : (
            <div className="space-y-4">
              {data.tests.recent.map((test) => (
                <div key={test.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(test.status)}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Test #{test.id.slice(-8)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(test.startedAt)} • {test.totalDocuments} dokumentů
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {test.status === 'completed' && (
                      <span className="text-sm font-medium text-gray-900">
                        {test.overallAccuracy.toFixed(1)}%
                      </span>
                    )}
                    {getStatusBadge(test.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Statistics */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Klíčové metriky</h3>
          
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Přesnost posledního testu</span>
                <span className="text-sm font-bold text-gray-900">
                  {data.tests.latestAccuracy.toFixed(1)}%
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${data.tests.latestAccuracy}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Průměrná počáteční přesnost</span>
                <span className="text-sm font-bold text-gray-900">
                  {data.statistics.averageInitialAccuracy.toFixed(1)}%
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${data.statistics.averageInitialAccuracy}%` }}
                ></div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Poslední spuštění testu</span>
                <span className="text-sm text-gray-500">
                  {data.tests.lastRunDate 
                    ? formatDate(data.tests.lastRunDate)
                    : 'Nikdy'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};